import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Megaphone,
  Plus,
  ShoppingCart,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Field, Input, MoneyInput, QuantityInput, Select, Textarea } from '@/ui/Field';
import { EmptyState, Page, PageBody, PageHeader, Panel } from '@/ui/Panel';
import { useShortcutHint } from '@/app/StatusLine';
import { useAtLeast } from '@/lib/auth';
import { api, errorMessage } from '@/lib/api/client';
import { dateTime, money, phone, qty } from '@/lib/format';
import { checkQuantity, quantityForRequest } from '@/lib/quantity';
import { parseQuantity, quantityText as formatQuantity } from '@kk-pos/shared/purchasing';
import type { Campaign, Customer, OrderDetail, ProductSummary } from '@/lib/api/types';
import { PAYMENT_METHODS, SUBSTITUTION_PREFS, ordersKey } from '@/features/orders/lib';
import { SearchPicker } from '@/features/orders/SearchPicker';
import {
  fetchActiveCampaigns,
  fetchCampaign,
  idNum,
  searchCustomers,
  searchProducts,
} from '@/features/orders/search';

/* The phone-order builder.
   Someone is on the phone; every field is one tab away and the estimate updates
   as lines land. Two exits: park it as a draft, or submit it into the pick
   queue — and if the submit leg fails the draft still exists, which the toast
   says out loud rather than pretending the order vanished. */

interface Line {
  productId: number;
  name: string;
  /** Shelf price in cents. Per-lb for weight items. */
  priceCents: number;
  soldByWeight: boolean;
  /**
   * Raw text of the quantity box (TASK-0228); read once more when the request
   * is built. Whole numbers on every line (`ORDER_QTY_UNIT`).
   */
  quantityText: string;
  /** Campaign override, when the product is on the selected campaign. */
  campaignPriceCents: number | null;
  maxQtyPerOrder: number | null;
}

/**
 * An order line's quantity is a count, weighed lines included: the order
 * engine reads a weighed line's orderedQty as pieces, estimates its weight from
 * the product's average weight and reserves that many units; the real weight
 * is set at pick (docs/specs/ORDER-ENGINE.md, Variable-Weight). So every box
 * takes whole numbers, and a weight typed into one is refused, not ordered as
 * that many pieces. TASK-0228 review F1.
 */
const ORDER_QTY_UNIT = 'ea' as const;

export const Route = createFileRoute('/_app/orders_/new')({
  staticData: { crumb: 'New order' },
  component: NewOrderScreen,
});

function NewOrderScreen() {
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const canUseCampaigns = useAtLeast('manager');

  useShortcutHint([{ keys: 'Esc', label: 'Back to orders' }]);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [campaignId, setCampaignId] = useState<string>('');
  const [lines, setLines] = useState<Line[]>([]);
  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'delivery'>('pickup');
  const [paymentMethod, setPaymentMethod] = useState<string>('pay_at_register');
  const [substitutionPref, setSubstitutionPref] = useState<string>('');
  const [requestedFor, setRequestedFor] = useState('');
  const [note, setNote] = useState('');

  const [deliveryName, setDeliveryName] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryAddress1, setDeliveryAddress1] = useState('');
  const [deliveryAddress2, setDeliveryAddress2] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryPostalCode, setDeliveryPostalCode] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [deliveryFeeCents, setDeliveryFeeCents] = useState<number | null>(null);
  const [tipCents, setTipCents] = useState<number | null>(null);

  // Campaigns are manager-gated on the server (`campaigns.read`), so the whole
  // picker is absent below that role rather than rendered and failing.
  const campaigns = useQuery({
    queryKey: ['campaigns', { active: true }],
    queryFn: fetchActiveCampaigns,
    enabled: canUseCampaigns,
    staleTime: 5 * 60_000,
  });

  const campaign = useQuery({
    queryKey: ['campaigns', Number(campaignId)],
    queryFn: () => fetchCampaign(Number(campaignId)),
    enabled: canUseCampaigns && campaignId !== '',
    staleTime: 60_000,
  });

  const campaignItems = useMemo(() => {
    const map = new Map<
      number,
      {
        priceCents: number | null;
        maxQty: number | null;
        name: string;
        productPrice: number;
        soldByWeight: boolean;
      }
    >();
    for (const item of campaign.data?.items ?? []) {
      map.set(item.productId, {
        priceCents: item.campaignPriceCents ?? null,
        maxQty: item.maxQtyPerOrder ?? null,
        name: item.productName,
        productPrice: item.productPrice,
        soldByWeight: !!item.soldByWeight,
      });
    }
    return map;
  }, [campaign.data]);

  function addProduct(product: {
    id: number;
    name: string;
    price: number;
    soldByWeight: boolean;
  }) {
    const campaignEntry = campaignItems.get(product.id);
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === product.id);
      if (existing) {
        // Adding the product again adds one. A blank box becomes 1; a box that
        // does not read is left for the operator to fix, not overwritten.
        return prev.map((l) => {
          if (l.productId !== product.id) return l;
          if (l.quantityText.trim() === '') return { ...l, quantityText: '1' };
          const current = parseQuantity(l.quantityText, ORDER_QTY_UNIT);
          return current === null
            ? l
            : { ...l, quantityText: formatQuantity(current + 1) };
        });
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          priceCents: product.price,
          soldByWeight: product.soldByWeight,
          quantityText: '1',
          campaignPriceCents: campaignEntry?.priceCents ?? null,
          maxQtyPerOrder: campaignEntry?.maxQty ?? null,
        },
      ];
    });
  }

  function setQuantityText(productId: number, text: string) {
    setLines((prev) =>
      prev.map((l) => (l.productId === productId ? { ...l, quantityText: text } : l))
    );
  }

  const unitFor = (line: Line) => line.campaignPriceCents ?? line.priceCents;

  const checks = lines.map((l) => checkQuantity(l.quantityText, ORDER_QTY_UNIT));
  /** What a line's box reads; null while blank or unreadable (display only). */
  const quantityAt = (index: number) => checks[index]?.quantity ?? null;

  // Weight lines are priced from an average weight the console does not hold,
  // so they are excluded rather than guessed at. Say so in the summary.
  const weightLines = lines.filter((l) => l.soldByWeight);
  const goodsCents = lines.reduce(
    (sum, l, i) => (l.soldByWeight ? sum : sum + unitFor(l) * (quantityAt(i) ?? 0)),
    0
  );
  const estimateCents = goodsCents + (deliveryFeeCents ?? 0) + (tipCents ?? 0);

  const overMax = lines.filter((l, i) => {
    const quantity = quantityAt(i);
    return l.maxQtyPerOrder !== null && quantity !== null && quantity > l.maxQtyPerOrder;
  });
  const quantityProblems = lines.flatMap((l, i) => {
    const check = checks[i]!;
    // A weight typed beside the "/ lb" price (it reads as lb, not as a count).
    if (
      check.problem !== null &&
      l.soldByWeight &&
      parseQuantity(l.quantityText, 'lb') !== null
    ) {
      return [
        `${l.name}: quantity takes whole numbers only. Weighed items are ordered by the piece; the weight is set at pick.`,
      ];
    }
    if (check.problem !== null) return [`${l.name}: quantity ${check.problem}.`];
    if (check.quantity <= 0) return [`${l.name}: quantity must be above zero.`];
    return [];
  });

  const deliveryIncomplete =
    fulfillmentType === 'delivery' && (!deliveryAddress1.trim() || !deliveryCity.trim());

  const blockers: string[] = [];
  if (lines.length === 0) blockers.push('Add at least one line.');
  if (overMax.length > 0) {
    blockers.push(
      `Over the campaign limit: ${overMax.map((l) => `${l.name} (max ${l.maxQtyPerOrder})`).join(', ')}.`
    );
  }
  if (deliveryIncomplete) blockers.push('A delivery needs a street address and a city.');
  blockers.push(...quantityProblems);

  function buildBody() {
    return {
      customerId: customer ? idNum(customer.id) : null,
      campaignId: campaignId ? Number(campaignId) : null,
      source: 'phone' as const,
      fulfillmentType,
      paymentMethod,
      substitutionPref: substitutionPref || null,
      requestedFor: requestedFor ? new Date(requestedFor).toISOString() : null,
      note: note.trim() || null,
      // The one conversion from typed text; throws rather than send a blank
      // or unreadable box.
      items: lines.map((l) => {
        const orderedQty = quantityForRequest(l.quantityText, ORDER_QTY_UNIT, l.name);
        if (orderedQty <= 0) throw new Error(`${l.name}: quantity must be above zero.`);
        return { productId: l.productId, orderedQty };
      }),
      ...(fulfillmentType === 'delivery'
        ? {
            deliveryName: deliveryName.trim() || null,
            deliveryPhone: deliveryPhone.trim() || null,
            deliveryAddress1: deliveryAddress1.trim() || null,
            deliveryAddress2: deliveryAddress2.trim() || null,
            deliveryCity: deliveryCity.trim() || null,
            deliveryState: deliveryState.trim() || null,
            deliveryPostalCode: deliveryPostalCode.trim() || null,
            deliveryInstructions: deliveryInstructions.trim() || null,
            deliveryFeeCents: deliveryFeeCents ?? 0,
            tipCents: tipCents ?? 0,
          }
        : {}),
    };
  }

  function afterCreate(order: OrderDetail) {
    qc.setQueryData(ordersKey.detail(order.id), order);
    void qc.invalidateQueries({ queryKey: ['orders', 'list'] });
    void qc.invalidateQueries({ queryKey: ['orders', 'counts'] });
    void qc.invalidateQueries({ queryKey: ['badges'] });
  }

  const saveDraft = useMutation({
    mutationFn: () => api.post<OrderDetail>('/orders', buildBody()),
    onSuccess: (order) => {
      afterCreate(order);
      toast.success(`${order.orderNumber} saved as a draft`);
      void navigate({ to: '/orders/$id', params: { id: String(order.id) } });
    },
    onError: (e) =>
      toast.error('Could not save the draft', { description: errorMessage(e) }),
  });

  const submitOrder = useMutation({
    mutationFn: async () => {
      const order = await api.post<OrderDetail>('/orders', buildBody());
      try {
        return await api.post<OrderDetail>(`/orders/${order.id}/submit`);
      } catch (err) {
        // The draft is real and saved; only the transition failed. Surface both
        // facts — losing an order the operator just typed is unforgivable.
        throw Object.assign(new Error(errorMessage(err)), { draft: order });
      }
    },
    onSuccess: (order) => {
      afterCreate(order);
      toast.success(`${order.orderNumber} submitted`, {
        description: 'Stock is reserved and it is in the pick queue.',
      });
      void navigate({ to: '/orders/$id', params: { id: String(order.id) } });
    },
    onError: (err) => {
      const draft = (err as { draft?: OrderDetail }).draft;
      if (draft) {
        afterCreate(draft);
        toast.error(`Saved as draft ${draft.orderNumber}, but the submit failed`, {
          description: `${errorMessage(err)} — the order is safe; submit it from the order screen.`,
        });
        void navigate({ to: '/orders/$id', params: { id: String(draft.id) } });
        return;
      }
      toast.error('Could not create the order', { description: errorMessage(err) });
    },
  });

  const busy = saveDraft.isPending || submitOrder.isPending;

  return (
    <Page>
      <PageHeader
        eyebrow="Orders"
        title="New phone order"
        subtitle={
          lines.length === 0
            ? 'Find the customer, then add what they are asking for.'
            : `${lines.length} ${lines.length === 1 ? 'line' : 'lines'} · estimated ${money(estimateCents)}${weightLines.length > 0 ? ' + weighed goods' : ''}`
        }
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              onClick={() => void navigate({ to: '/orders' })}
            >
              Orders
            </Button>
            <Button
              size="sm"
              disabled={blockers.length > 0}
              loading={saveDraft.isPending}
              onClick={() => saveDraft.mutate()}
            >
              Save draft
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={blockers.length > 0 || busy}
              loading={submitOrder.isPending}
              onClick={() => submitOrder.mutate()}
            >
              Submit order
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_24rem]">
          {/* ── Left: who and what ───────────────────────────────────────── */}
          <div className="min-w-0 space-y-3">
            <Panel title="Customer" description="Optional — leave blank for a walk-in">
              {customer ? (
                <div className="flex items-start gap-2">
                  <UserRound className="text-ink-faint mt-0.5 size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-ink truncate text-sm">
                      {customer.companyName || customer.name}
                    </div>
                    <div className="text-2xs text-ink-faint mt-0.5 flex flex-wrap items-center gap-2">
                      {customer.accountNumber ? (
                        <Code>{customer.accountNumber}</Code>
                      ) : null}
                      {customer.phone ? <span>{phone(customer.phone)}</span> : null}
                      <span className="font-mono">Balance {money(customer.balance)}</span>
                      {customer.hasCard ? (
                        <Badge tone="info">Card ····{customer.cardLastFour}</Badge>
                      ) : null}
                      {customer.taxExempt ? (
                        <Badge tone="neutral">Tax exempt</Badge>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    title="Clear the customer"
                    onClick={() => setCustomer(null)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              ) : (
                <SearchPicker<Customer>
                  autoFocus
                  queryKey={['customers', 'search']}
                  search={(term, signal) => searchCustomers(term, signal)}
                  itemKey={(c) => c.id}
                  onSelect={(c) => {
                    setCustomer(c);
                    if (!deliveryName) setDeliveryName(c.companyName || c.name);
                    if (!deliveryPhone && c.phone) setDeliveryPhone(c.phone);
                  }}
                  placeholder="Name, phone, account number or company…"
                  renderItem={(c) => (
                    <>
                      <span className="min-w-0 flex-1 truncate">
                        {c.companyName || c.name}
                      </span>
                      {c.phone ? (
                        <span className="text-2xs shrink-0 font-mono">
                          {phone(c.phone)}
                        </span>
                      ) : null}
                      <span className="text-2xs shrink-0 font-mono tabular-nums">
                        {money(c.balance)}
                      </span>
                    </>
                  )}
                />
              )}
            </Panel>

            {canUseCampaigns ? (
              <Panel
                title="Campaign"
                description="Campaign pricing and per-order limits apply to listed products"
                actions={
                  campaignId ? (
                    <Button size="xs" variant="ghost" onClick={() => setCampaignId('')}>
                      Clear
                    </Button>
                  ) : null
                }
              >
                <Select
                  size="sm"
                  value={campaignId}
                  aria-label="Campaign"
                  onChange={(e) => setCampaignId(e.currentTarget.value)}
                >
                  <option value="">No campaign</option>
                  {(campaigns.data ?? []).map((c: Campaign) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · cutoff {dateTime(c.cutoffAt)}
                    </option>
                  ))}
                </Select>

                {campaign.data ? (
                  <div className="mt-2">
                    <div className="text-2xs text-ink-faint mb-1 flex items-center gap-1.5">
                      <Megaphone className="size-3" />
                      {campaign.data.items?.length ?? 0} products · cutoff{' '}
                      {dateTime(campaign.data.cutoffAt)}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(campaign.data.items ?? []).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() =>
                            addProduct({
                              id: item.productId,
                              name: item.productName,
                              price: item.productPrice,
                              soldByWeight: !!item.soldByWeight,
                            })
                          }
                          className="border-line bg-surface text-2xs text-ink-muted hover:border-line-strong hover:text-ink flex h-6 items-center gap-1.5 rounded-sm border px-1.5"
                        >
                          <Plus className="size-3" />
                          {item.productName}
                          <span className="text-signal-ink font-mono tabular-nums">
                            {money(item.campaignPriceCents ?? item.productPrice)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Panel>
            ) : null}

            <Panel title="Add products">
              <SearchPicker<ProductSummary>
                queryKey={['products', 'search']}
                clearOnSelect
                search={(term, signal) => searchProducts(term, signal)}
                itemKey={(p) => p.id}
                onSelect={(p) =>
                  addProduct({
                    id: idNum(p.id),
                    name: p.name,
                    price: p.price,
                    soldByWeight: !!p.soldByWeight,
                  })
                }
                placeholder="Search the catalogue by name or SKU…"
                renderItem={(p) => (
                  <>
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.soldByWeight ? (
                      <span className="text-2xs shrink-0">/lb</span>
                    ) : null}
                    {campaignItems.has(idNum(p.id)) ? (
                      <span className="text-2xs text-signal-ink shrink-0">campaign</span>
                    ) : null}
                    <span className="text-2xs shrink-0 font-mono tabular-nums">
                      {money(p.price)}
                    </span>
                  </>
                )}
              />
            </Panel>

            <Panel
              title="Lines"
              description={
                lines.length === 0 ? undefined : `${lines.length} on this order`
              }
              bleed
            >
              {lines.length === 0 ? (
                <EmptyState
                  compact
                  icon={<ShoppingCart />}
                  title="Nothing on the order yet"
                  description="Search above, or click a campaign product."
                />
              ) : (
                <div className="divide-line-soft divide-y">
                  {lines.map((line, index) => {
                    const quantity = quantityAt(index);
                    const over =
                      line.maxQtyPerOrder !== null &&
                      quantity !== null &&
                      quantity > line.maxQtyPerOrder;
                    return (
                      <div
                        key={line.productId}
                        className="grid grid-cols-[minmax(0,1fr)_6rem_6.5rem_2rem] items-center gap-2 px-3 py-1.5"
                      >
                        <div className="min-w-0">
                          <div className="text-ink truncate text-xs">{line.name}</div>
                          <div className="text-2xs text-ink-faint mt-0.5 flex items-center gap-2">
                            <span className="font-mono">
                              {money(unitFor(line))}
                              {line.soldByWeight ? ' / lb' : ' ea'}
                            </span>
                            {line.campaignPriceCents !== null ? (
                              <Badge tone="signal">Campaign</Badge>
                            ) : null}
                            {line.maxQtyPerOrder !== null ? (
                              <span className={over ? 'text-negative' : undefined}>
                                max {line.maxQtyPerOrder}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <QuantityInput
                          size="sm"
                          unit={ORDER_QTY_UNIT}
                          aria-label={`Quantity for ${line.name}`}
                          invalid={over || !(quantity !== null && quantity > 0)}
                          value={line.quantityText}
                          onValueChange={(text) => setQuantityText(line.productId, text)}
                        />
                        <div className="text-ink text-right font-mono text-xs tabular-nums">
                          {line.soldByWeight ? (
                            <span className="text-2xs text-ink-faint">at pick</span>
                          ) : quantity === null ? (
                            '—'
                          ) : (
                            money(unitFor(line) * quantity)
                          )}
                        </div>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          title="Remove line"
                          onClick={() =>
                            setLines((prev) =>
                              prev.filter((l) => l.productId !== line.productId)
                            )
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>

          {/* ── Right: how and when ──────────────────────────────────────── */}
          <div className="min-w-0 space-y-3">
            <Panel title="Fulfilment">
              <div className="space-y-3">
                <Field label="Type">
                  <Select
                    value={fulfillmentType}
                    onChange={(e) =>
                      setFulfillmentType(e.currentTarget.value as 'pickup' | 'delivery')
                    }
                  >
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                  </Select>
                </Field>
                <Field
                  label="Requested for"
                  hint="When the customer wants it. Drives the overdue rail on the queue."
                >
                  <Input
                    type="datetime-local"
                    value={requestedFor}
                    onChange={(e) => setRequestedFor(e.currentTarget.value)}
                  />
                </Field>
                <Field label="Payment" hint="Take payment at a register.">
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.currentTarget.value)}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Substitutions"
                  hint="Blank inherits the customer's own preference."
                >
                  <Select
                    value={substitutionPref}
                    onChange={(e) => setSubstitutionPref(e.currentTarget.value)}
                  >
                    <option value="">Customer default</option>
                    {SUBSTITUTION_PREFS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Note">
                  <Textarea
                    rows={2}
                    value={note}
                    placeholder="Ring the bell twice…"
                    onChange={(e) => setNote(e.currentTarget.value)}
                  />
                </Field>
              </div>
            </Panel>

            {fulfillmentType === 'delivery' ? (
              <Panel title="Delivery">
                <div className="space-y-3">
                  <Field label="Recipient">
                    <Input
                      value={deliveryName}
                      onChange={(e) => setDeliveryName(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      mono
                      value={deliveryPhone}
                      onChange={(e) => setDeliveryPhone(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="Address" required>
                    <Input
                      value={deliveryAddress1}
                      placeholder="Street and number"
                      invalid={fulfillmentType === 'delivery' && !deliveryAddress1.trim()}
                      onChange={(e) => setDeliveryAddress1(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="Address line 2">
                    <Input
                      value={deliveryAddress2}
                      placeholder="Apartment, suite…"
                      onChange={(e) => setDeliveryAddress2(e.currentTarget.value)}
                    />
                  </Field>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="City" required className="col-span-2">
                      <Input
                        value={deliveryCity}
                        invalid={fulfillmentType === 'delivery' && !deliveryCity.trim()}
                        onChange={(e) => setDeliveryCity(e.currentTarget.value)}
                      />
                    </Field>
                    <Field label="State">
                      <Input
                        value={deliveryState}
                        maxLength={64}
                        onChange={(e) => setDeliveryState(e.currentTarget.value)}
                      />
                    </Field>
                  </div>
                  <Field label="Postal code">
                    <Input
                      mono
                      value={deliveryPostalCode}
                      maxLength={20}
                      onChange={(e) => setDeliveryPostalCode(e.currentTarget.value)}
                    />
                  </Field>
                  <Field label="Instructions">
                    <Textarea
                      rows={2}
                      value={deliveryInstructions}
                      placeholder="Gate code, where to leave it…"
                      onChange={(e) => setDeliveryInstructions(e.currentTarget.value)}
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Delivery fee">
                      <MoneyInput
                        value={deliveryFeeCents}
                        onValueChange={setDeliveryFeeCents}
                      />
                    </Field>
                    <Field label="Tip">
                      <MoneyInput value={tipCents} onValueChange={setTipCents} />
                    </Field>
                  </div>
                </div>
              </Panel>
            ) : null}

            <Panel
              title="Estimate"
              footer={
                weightLines.length > 0 ? (
                  <span>
                    {weightLines.length} weighed{' '}
                    {weightLines.length === 1 ? 'line is' : 'lines are'} priced at pick
                    and are not in this figure.
                  </span>
                ) : (
                  <span>Tax is applied by the server at settle time.</span>
                )
              }
            >
              <dl className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <dt className="text-ink-muted">Goods</dt>
                  <dd className="text-ink font-mono tabular-nums">{money(goodsCents)}</dd>
                </div>
                {fulfillmentType === 'delivery' ? (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-ink-muted">Delivery fee</dt>
                      <dd className="text-ink font-mono tabular-nums">
                        {money(deliveryFeeCents ?? 0)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-ink-muted">Tip</dt>
                      <dd className="text-ink font-mono tabular-nums">
                        {money(tipCents ?? 0)}
                      </dd>
                    </div>
                  </>
                ) : null}
                <div className="border-line-soft flex justify-between border-t pt-1 text-sm">
                  <dt className="text-ink">Estimated total</dt>
                  <dd className="text-ink font-mono font-medium tabular-nums">
                    {money(estimateCents)}
                  </dd>
                </div>
              </dl>

              {blockers.length > 0 ? (
                <ul className="border-warn/30 bg-warn-muted text-2xs text-ink mt-2 space-y-1 rounded border p-2">
                  {blockers.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : null}

              {lines.length > 0 ? (
                <div className="text-2xs text-ink-faint mt-2">
                  {qty(lines.reduce((s, _, i) => s + (quantityAt(i) ?? 0), 0))} units
                  across {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                </div>
              ) : null}
            </Panel>
          </div>
        </div>
      </PageBody>
    </Page>
  );
}
